import { Theme, useTheme } from '@mui/material/styles';
import { ClassNameMap, makeStyles } from '@mui/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Home as HomeIcon, Logout, Settings } from '@mui/icons-material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import {
  useCallback,
  useMemo,
  useState,
  ReactElement,
  cloneElement,
  useEffect,
  useRef,
} from 'react';
import SideMenu from './side-menu';
import {
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  useMediaQuery,
  useScrollTrigger,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { __AUTH_API_URL__, __ENVIRONMENT_TYPE__ } from 'shell/constants/environment';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import classNames from 'classnames';
import { useBus } from 'shell/hooks/use-bus';
import { NAVBAR_HEIGHT_EVENT, SIDEBAR_OPEN_EVENT } from 'shell/constants/events';
import { useElementHeightObserver } from 'shell/hooks/use-element-observer';
import { Enviroment } from 'shell/types/environment';

interface StyleProps {
  open: boolean;
  fullScreen: boolean;
}

// The width of side menu in pxel.
const sideMenuWidth = 210;

const useStyles = makeStyles((theme: Theme) => {
  return {
    homeButton: {
      marginLeft: theme.spacing(0.1),
      marginRight: theme.spacing(2),
      color: `${theme.palette.common.white} !important`,
    },
    menuButton: {
      marginLeft: theme.spacing(0.1),
      marginRight: theme.spacing(2),
    },
    imageHeader: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingRight: theme.spacing(2),
    },
    header: {
      width: '4rem',
    },
    title: {
      flexGrow: 1,
    },
    appBarWrapper: (props: StyleProps) => ({
      marginLeft: 0,
      transition: `${theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: 0,
      })}`,
      ...(props.open
        ? {
            width: props.fullScreen ? '' : `calc(100% - ${sideMenuWidth}px)`,
            marginLeft: props.fullScreen ? '' : `${sideMenuWidth}px`,
            transition: theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }
        : {}),
    }),
    appBar: {
      backgroundColor: `${theme.palette.cdmBrand.main} !important`,
    },
    main: ({ open, fullScreen }: StyleProps) => ({
      marginLeft: 0,
      flex: '1 1 auto',
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      ...(open && {
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: fullScreen ? '' : `${sideMenuWidth}px`,
      }),
    }),
  };
});

interface Props {
  window?: () => Window;
  children: React.ReactElement;
  setOpen: (open: boolean) => void;
}

function ElevationScroll(props: Props) {
  const { children, window, setOpen } = props;

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window ? window() : undefined,
  });

  useEffect(() => {
    if (trigger) {
      setOpen(false);
    }
  }, [trigger]);

  return cloneElement(children, {
    // elevation: trigger ? 4 : 0,
    // position: trigger ? 'fixed' : 'static',
  });
}

export default function Navbar({ children }: { children: ReactElement }): React.ReactElement {
  const { setUserProfile, userProfile } = useUserProfile();
  const theme = useTheme();

  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  const onLogOff = useCallback(() => {
    setUserProfile();
    window.location.href = `${__AUTH_API_URL__}/operators/logoff`;
  }, [setUserProfile]);

  /**
   * The today date.
   */
  const todayDate = useMemo(() => DateTime.now().toFormat('EEEE, dd/MM/yyyy'), []);

  /**
   * A flag telling if the side navigation menu is opened.
   */
  const [menuVisible, setMenuVisibility] = useState(false);

  /**
   * The html element acting as anchor of the profile menu.
   */
  const [profileMenunchorEl, setProfileMenuAnchorEl] = useState<null | HTMLElement>(null);

  /**
   * A flag telling if the profile dropdown is visible.
   */
  const profileMenuVisible = !!profileMenunchorEl;

  /**
   * A set of css classes.
   */
  const classes: ClassNameMap<string> = useStyles({ open: menuVisible, fullScreen });

  /**
   * Handles the click on profile icon.
   */
  const handleProfileIconClick = useCallback(
    (event: React.MouseEvent<HTMLElement>): void => {
      setProfileMenuAnchorEl(event.currentTarget);
    },
    [setProfileMenuAnchorEl]
  );

  const navRef = useRef<HTMLDivElement | null>(null);

  const eventBus = useBus();

  const updateMenuVisibility = useCallback(
    (open) => {
      setMenuVisibility(open);
      eventBus.emit(SIDEBAR_OPEN_EVENT, open);
    },
    [setMenuVisibility, eventBus]
  );

  useElementHeightObserver(navRef.current as HTMLElement, (newNavHeight) => {
    eventBus.emit(NAVBAR_HEIGHT_EVENT, newNavHeight);
  });

  return (
    <div className={classNames('pmi-h-100 pmi-d-flex pmi-flex-column', classes.root)}>
      <div className={classes.appBarWrapper}>
        <ElevationScroll setOpen={updateMenuVisibility}>
          <AppBar
            position={menuVisible ? 'static' : 'fixed'}
            className={classes.appBar}
            ref={navRef}
            id="navigation-menu"
          >
            <Toolbar className="pmi-d-flex pmi-justify-content-between">
              <div className="pmi-d-flex pmi-justify-start pmi-align-items-center">
                {!menuVisible ? (
                  <div
                    className={classNames(
                      classes.header,
                      'pmi-d-flex pmi-justify-start pmi-align-items-center'
                    )}
                  >
                    <div className={classes.imageHeader}>
                      <img src="/images/logo-cdm.png" alt="Logo Comune Milano" width="100%" />
                    </div>
                  </div>
                ) : null}
                <div className="pmi-d-flex pmi-align-items-center">
                  {!menuVisible ? (
                    <IconButton
                      edge="start"
                      color="inherit"
                      aria-label="menu"
                      about="auto"
                      onClick={() => updateMenuVisibility(!menuVisible)}
                      className={classes.menuButton}
                    >
                      {menuVisible ? <CloseIcon /> : <MenuIcon />}
                    </IconButton>
                  ) : null}
                  <Typography
                    fontWeight="bold"
                    variant="h5"
                    marginRight={theme.spacing(2)}
                    className="notranslate"
                  >
                    S.I.De.
                  </Typography>
                  <Link to="/">
                    <IconButton
                      edge="start"
                      className={classes.homeButton}
                      aria-label="menu"
                      about="auto"
                    >
                      <HomeIcon />
                    </IconButton>
                  </Link>
                  {!fullScreen ? (
                    <Typography variant="subtitle2" marginRight={theme.spacing(2)}>
                      {todayDate}
                    </Typography>
                  ) : undefined}
                  <Divider orientation="vertical" flexItem />
                  <Typography variant="subtitle1" marginLeft={theme.spacing(2)}>
                    {userProfile.datiLogin?.profile?.servizio?.descrizione || ''}
                  </Typography>
                  <Typography
                    fontWeight="bold"
                    variant="h5"
                    marginLeft={theme.spacing(2)}
                    style={{ textTransform: 'uppercase' }}
                  >
                    {__ENVIRONMENT_TYPE__ === Enviroment.Development
                      ? 'Sviluppo'
                      : __ENVIRONMENT_TYPE__ === Enviroment.Staging
                      ? 'Collaudo'
                      : ''}
                  </Typography>
                </div>
              </div>
              <div className="pmi-d-flex pmi-align-items-center">
                {!fullScreen ? (
                  <Typography variant="subtitle1" marginLeft={theme.spacing(2)}>
                    {userProfile.datiLogin?.nome || ''}
                  </Typography>
                ) : undefined}
                <IconButton
                  aria-label="Opzioni profilo utente"
                  aria-controls="profile-menu"
                  aria-haspopup="true"
                  color="inherit"
                  onClick={handleProfileIconClick}
                >
                  <AccountCircleIcon />
                </IconButton>
                <Menu
                  id="profile-menu"
                  anchorEl={profileMenunchorEl}
                  open={profileMenuVisible}
                  onClose={() => setProfileMenuAnchorEl(null)}
                >
                  <MenuItem disabled>{userProfile.datiLogin?.nome || ''}</MenuItem>
                  <Divider />
                  <MenuItem>
                    <ListItemIcon>
                      <Settings fontSize="small" />
                    </ListItemIcon>
                    Gestisci account
                  </MenuItem>
                  <MenuItem onClick={onLogOff}>
                    <ListItemIcon>
                      <Logout fontSize="small" />
                    </ListItemIcon>
                    Logout
                  </MenuItem>
                </Menu>
              </div>
            </Toolbar>
          </AppBar>
        </ElevationScroll>
      </div>
      <SideMenu
        visible={menuVisible}
        onClose={() => updateMenuVisibility(false)}
        width={sideMenuWidth}
      />
      <div className={classNames('pmi-d-flex pmi-flex-column', classes.main)}>{children}</div>
    </div>
  );
}

Navbar.displayName = 'Navbar component';
